import * as React from "react"
import {
  AlertTriangle,
  CheckCircle2,
  CircleDollarSign,
  Crown,
  Gauge,
  Grip,
  Lock,
  Plus,
  RefreshCcw,
  RotateCcw,
  Save,
  ShieldCheck,
  Trash2,
} from "lucide-react"
import { useAuth, type AdminPermissions } from "@/contexts/AuthContext"
import {
  cloneDefaultOvalClubSettings,
  type LiveGamePrizeConfig,
  type LiveGamePrizeType,
  normalizeOvalClubSettings,
  type MinesCashoutRow,
  type OvalClubSettings,
  type RafflePrize,
  type RankCashBracket,
  type RankTicketBracket,
  type TierUnlock,
  type WelcomeSpinPrizeConfig,
} from "@/lib/ovalClubSettings"
import { getDefaultOutcomeRanges } from "@/lib/slotMachineTypes"
import { AdminCard } from "./AdminCard"
import { SimpleToggles } from "./SimpleToggles"

interface AdminEconomicsProps {
  permissions: AdminPermissions | null
}

type SeasonPeriod = "weekly" | "monthly" | "seasonal"
type SectionId = "foundation" | "rewards" | "qualification" | "events" | "games" | "throttles" | "governance"

const seasonPeriods: SeasonPeriod[] = ["weekly", "monthly", "seasonal"]

const sectionTabs: Array<{ id: SectionId; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: "foundation", label: "Foundation", icon: Gauge },
  { id: "rewards", label: "Rewards", icon: CircleDollarSign },
  { id: "qualification", label: "Qualification", icon: Grip },
  { id: "events", label: "Events", icon: Crown },
  { id: "games", label: "Games", icon: CircleDollarSign },
  { id: "throttles", label: "Throttles", icon: Gauge },
  { id: "governance", label: "Governance", icon: ShieldCheck },
]

const sectionImpactNotes: Record<SectionId, { title: string; affects: string[]; caution?: string }> = {
  foundation: {
    title: "Core economy baselines and platform-wide reward policy defaults",
    affects: [
      "Points economy targets, budgets, season behavior, and leaderboard cadence defaults.",
      "Cash-control rules and high-level policy toggles used as admin configuration source-of-truth.",
      "Does not retroactively recalculate past user rewards or balances.",
    ],
  },
  rewards: {
    title: "Leaderboard, raffle, and KOTH payout structures",
    affects: [
      "Prize ladders, raffle prize pools, rank brackets, and champion prize options stored in economics settings.",
      "Weekly raffle generation reads the weekly individual raffle prize configuration from these settings.",
      "Changes affect future draws/payout calculations, not already completed draws.",
    ],
  },
  qualification: {
    title: "Eligibility gates for rewards, raffles, and KOTH",
    affects: [
      "Minimum deals/points and qualification rules used to determine who can earn rewards.",
      "Raffle eligibility and king-of-the-hill qualification requirements.",
      "Primarily impacts future qualification checks and reward access decisions.",
    ],
  },
  events: {
    title: "Program cadence and event frequency controls",
    affects: [
      "Leaderboard/event cadence behavior and free-spin cadence values mirrored into game settings.",
      "Operational timing/availability knobs for recurring reward events.",
      "Use with Games section when changing spin frequency and odds together.",
    ],
  },
  games: {
    title: "Game configuration and live prize tables",
    affects: [
      "Live Reward Prize Tables directly power server-side deal slot rewards, free spin rewards, and onboarding welcome spin rewards.",
      "Mines/slot/wheel economics tables are persisted tuning settings for game behavior and planning; some are not yet consumed by every runtime path.",
      "Wheel free spins/day is mirrored with Event Cadence and impacts user spin availability rules.",
    ],
    caution: "For immediate gameplay verification, test the Live Reward Prize Tables first. Treat other game tables as tuning/model inputs unless you have a runtime path confirmed.",
  },
  throttles: {
    title: "Burn-rate response and game tightening rules",
    affects: [
      "How the system should respond when reward burn rises (normal/soft/hard modes).",
      "Throttle policies for slot/mines/wheel probabilities and redistributions stored for runtime/control logic.",
      "Best tested with instrumentation/admin metrics because many values are conditional on trigger thresholds.",
    ],
  },
  governance: {
    title: "Integrity, enforcement separation, and internal dashboard language/metrics",
    affects: [
      "Fraud/integrity policy toggles and operational dashboard metric lists.",
      "Internal/admin-facing behavior and language guidance rather than direct user prize outcomes.",
      "Safe to test by save/reload persistence and admin UI consumers that read these settings.",
    ],
  },
}

const sectionRuntimeWiring: Record<SectionId, Array<{ label: string; status: "active" | "not_active"; note?: string }>> = {
  foundation: [
    { label: "Core economy fields (points/budget/season targets)", status: "not_active", note: "Persisted config; no confirmed runtime consumer path wired yet." },
    { label: "Cash-control policy toggles", status: "not_active", note: "Saved in economics settings, but not confirmed as live-enforced across frontend flows." },
  ],
  rewards: [
    { label: "Weekly individual raffle prize definitions", status: "active", note: "Used by weekly raffle draw/status logic." },
    { label: "Monthly/seasonal raffle prize definitions", status: "not_active", note: "Saved, but no confirmed runtime draw consumer wired yet." },
    { label: "Leaderboard guaranteed cash / ticket brackets", status: "not_active", note: "Editable and persisted; no confirmed frontend/runtime payout engine wired yet." },
    { label: "King of the Hill champion prize options", status: "not_active", note: "Stored config only at the moment." },
  ],
  qualification: [
    { label: "Weekly individual min deals for raffle eligibility", status: "active", note: "Read by weekly raffle status/draw logic." },
    { label: "Other individual/team qualification gates", status: "not_active", note: "Saved config; broader runtime qualification enforcement not fully wired yet." },
    { label: "Raffle eligibility policy toggles", status: "not_active", note: "Persisted, but not confirmed in active reward eligibility pipeline yet." },
    { label: "King of the Hill qualification gates", status: "not_active", note: "Stored config only at the moment." },
  ],
  events: [
    { label: "Daily wheel free spins/check-in settings (admin mirrored values)", status: "not_active", note: "Values sync across admin sections but runtime accrual path is not confirmed wired here." },
    { label: "Leaderboard / KOTH cadence settings", status: "not_active", note: "Persisted configuration for future/live scheduling logic." },
  ],
  games: [
    { label: "Deal slot rewards (Live Reward Prize Tables)", status: "active", note: "Server-side reward roll uses admin-configured prizes + percentages." },
    { label: "Free spin rewards (Live Reward Prize Tables)", status: "active", note: "Server-side free-spin roll uses admin-configured prizes + percentages." },
    { label: "Welcome spin rewards (Live Reward Prize Tables)", status: "active", note: "Server-side onboarding spin + frontend ladder use admin-configured prizes + percentages." },
    { label: "Slot probability table (`games.slot.probabilities`)", status: "active", note: "Drives Deals tab Casino Slots mini-game odds." },
    { label: "Wheel outcomes (`games.wheel.outcomes`)", status: "not_active", note: "Saved and editable, but not wired to a runtime wheel selector yet." },
    { label: "Mines config tables", status: "not_active", note: "Persisted config; no confirmed runtime consumer wired yet." },
    { label: "Slot wagers / EV / house edge display fields", status: "not_active", note: "Editable and persisted; not confirmed as live gameplay controls." },
  ],
  throttles: [
    { label: "Burn trigger thresholds", status: "not_active", note: "Persisted config; runtime throttle engine wiring not confirmed." },
    { label: "Soft/hard tighten slot/wheel/mines controls", status: "not_active", note: "Stored tuning config for future/partial backend throttle logic." },
  ],
  governance: [
    { label: "Integrity/fraud toggle settings", status: "not_active", note: "Saved in economics settings; no confirmed direct frontend/runtime enforcement hookup from this page." },
    { label: "Internal dashboard metric lists", status: "not_active", note: "Admin metadata/config only." },
    { label: "UI language helper lists", status: "not_active", note: "Persisted config only." },
  ],
}

const asNumber = (value: string, fallback = 0): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const fmtCompact = (value: number): string => {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value)
}

const sumProbabilityRows = (rows: Array<{ probabilityPct: number }>): number =>
  rows.reduce((sum, row) => sum + (Number.isFinite(row.probabilityPct) ? row.probabilityPct : 0), 0)

const periodLabel = (period: SeasonPeriod): string =>
  period.charAt(0).toUpperCase() + period.slice(1)

const toDisplayString = (value: unknown): string | null => {
  if (value === null || value === undefined || value === "") {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString()
  }

  if (typeof value === "string") {
    return value
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }

  if (typeof value === "object" && "toString" in value) {
    const text = String(value)
    return text === "[object Object]" ? null : text
  }

  return null
}

function SectionTabButton({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean
  icon: React.ComponentType<{ className?: string }>
  label: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-all duration-200 font-admin border",
        active
          ? "bg-[#E8F1FF] text-[#0A2540] border-[#BFD8FF] shadow-[0_6px_20px_rgba(120,160,220,0.35)]"
          : "bg-white/[0.06] text-white/70 border-white/10 hover:text-white hover:border-white/20",
      ].join(" ")}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  )
}

function SectionImpactCard({ section }: { section: SectionId }) {
  const note = sectionImpactNotes[section]

  return (
    <AdminCard className="bg-[#9EC6FF]/[0.05] border-[#9EC6FF]/20">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-[#9EC6FF]/10 border border-[#9EC6FF]/20 flex items-center justify-center shrink-0">
          <CircleDollarSign className="w-4 h-4 text-[#CDE2FF]" />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm text-white/90 font-admin">{note.title}</h4>
          <ul className="mt-2 space-y-1.5">
            {note.affects.map((line) => (
              <li key={line} className="text-xs text-white/65 font-admin leading-relaxed">
                {line}
              </li>
            ))}
          </ul>
          {note.caution && (
            <div className="mt-3 rounded-xl border border-yellow-300/20 bg-yellow-400/5 px-3 py-2 text-xs text-yellow-100/90 font-admin">
              {note.caution}
            </div>
          )}
        </div>
      </div>
    </AdminCard>
  )
}

function ProbabilityTotalBadge({
  total,
  target = 100,
  className = "",
}: {
  total: number
  target?: number
  className?: string
}) {
  const diff = Math.abs(total - target)
  const isOk = diff <= 0.001
  const isNear = !isOk && diff <= 0.5

  const toneClass = isOk
    ? "border-emerald-400/20 bg-emerald-400/[0.08] text-emerald-300"
    : isNear
      ? "border-yellow-400/20 bg-yellow-400/[0.08] text-yellow-300"
      : "border-red-400/20 bg-red-400/[0.08] text-red-300"

  const dotColor = isOk ? "bg-emerald-400" : isNear ? "bg-yellow-400" : "bg-red-400"
  const statusLabel = isOk ? "OK" : isNear ? "Near" : "Mismatch"

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-medium font-admin tabular-nums ${toneClass} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor} ${isOk ? "" : "animate-pulse"}`} />
      <span>{total.toFixed(3)}%</span>
      <span className="opacity-60">·</span>
      <span className="opacity-70">{statusLabel}</span>
    </span>
  )
}

function FeatureStatusBadge({
  status,
  className = "",
}: {
  status: "active" | "not_active"
  className?: string
}) {
  const isActive = status === "active"
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-admin",
        isActive
          ? "border-emerald-300/25 bg-emerald-400/10 text-emerald-100"
          : "border-yellow-300/25 bg-yellow-400/10 text-yellow-100",
        className,
      ].join(" ")}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${isActive ? "bg-emerald-300" : "bg-yellow-300"}`} />
      {isActive ? "ACTIVE" : "NOT ACTIVE YET"}
    </span>
  )
}

function SectionBlockTitle({
  title,
  status,
  className = "",
}: {
  title: string
  status?: "active" | "not_active"
  className?: string
}) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <h4 className="text-sm text-white/90 font-admin">{title}</h4>
      {status && <FeatureStatusBadge status={status} />}
    </div>
  )
}

function SectionRuntimeStatusCard({ section }: { section: SectionId }) {
  const rows = sectionRuntimeWiring[section] ?? []

  return (
    <AdminCard className="bg-white/[0.03] border-white/10">
      <div className="flex items-center gap-2 mb-3">
        <h4 className="text-sm text-white/90 font-admin">Runtime Wiring Status</h4>
      </div>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={`${section}-${row.label}`} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-white/90 font-admin leading-relaxed">{row.label}</p>
                {row.note && <p className="text-[11px] text-white/50 font-admin mt-1 leading-relaxed">{row.note}</p>}
              </div>
              <FeatureStatusBadge status={row.status} className="shrink-0" />
            </div>
          </div>
        ))}
      </div>
    </AdminCard>
  )
}

function ToggleField({
  label,
  description,
  value,
  onChange,
  disabled,
}: {
  label: string
  description?: string
  value: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-white/90 font-admin">{label}</p>
          {description && <p className="text-xs text-white/45 mt-0.5 font-admin">{description}</p>}
        </div>
        <button
          type="button"
          onClick={() => onChange(!value)}
          disabled={disabled}
          className={[
            "relative h-8 w-14 rounded-full transition-colors duration-200 overflow-hidden",
            value ? "bg-[#F37020]" : "bg-white/20",
            disabled ? "opacity-50 cursor-not-allowed" : "",
          ].join(" ")}
        >
          <span
            className={[
              "absolute left-1 top-1 h-6 w-6 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.35)] transition-transform duration-200",
              value ? "translate-x-6" : "translate-x-0",
            ].join(" ")}
          />
        </button>
      </div>
    </div>
  )
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step,
  suffix,
  disabled,
}: {
  label: string
  value: number
  onChange: (next: number) => void
  min?: number
  max?: number
  step?: number
  suffix?: string
  disabled?: boolean
}) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs uppercase tracking-wide text-white/45 font-admin">{label}</span>
      <div className="relative">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step ?? 1}
          disabled={disabled}
          onChange={(event) => onChange(asNumber(event.target.value, value))}
          className="w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-sm text-white font-admin outline-none focus:border-[#F37020] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/45 font-admin">{suffix}</span>
        )}
      </div>
    </label>
  )
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string
  value: T
  options: T[]
  onChange: (next: T) => void
  disabled?: boolean
}) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs uppercase tracking-wide text-white/45 font-admin">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
        disabled={disabled}
        className="w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-sm text-white font-admin outline-none focus:border-[#F37020] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {options.map((option) => (
          <option key={option} value={option} className="bg-[#0F1219] text-white">
            {option}
          </option>
        ))}
      </select>
    </label>
  )
}

function TierRowsEditor({
  title,
  status,
  rows,
  onChange,
  onAdd,
  onRemove,
  disabled,
}: {
  title: string
  status?: "active" | "not_active"
  rows: TierUnlock[]
  onChange: (index: number, patch: Partial<TierUnlock>) => void
  onAdd: () => void
  onRemove: (index: number) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-center justify-between">
        <SectionBlockTitle title={title} status={status} />
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-admin"
        >
          <Plus className="w-3.5 h-3.5" /> Add Row
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {rows.map((row, index) => (
          <div key={`${title}-${index}`} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-5">
              <NumberField
                label="Points"
                value={row.points}
                onChange={(value) => onChange(index, { points: value })}
                disabled={disabled}
              />
            </div>
            <div className="col-span-5">
              <NumberField
                label="Tickets"
                value={row.tickets}
                onChange={(value) => onChange(index, { tickets: value })}
                disabled={disabled}
              />
            </div>
            <div className="col-span-2">
              <button
                type="button"
                onClick={() => onRemove(index)}
                disabled={disabled || rows.length <= 1}
                className="w-full rounded-xl border border-red-300/20 bg-red-400/5 px-2 py-2 text-red-200 hover:text-red-100 hover:border-red-200/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CashBracketEditor({
  title,
  status,
  rows,
  onChange,
  onAdd,
  onRemove,
  disabled,
}: {
  title: string
  status?: "active" | "not_active"
  rows: RankCashBracket[]
  onChange: (index: number, patch: Partial<RankCashBracket>) => void
  onAdd: () => void
  onRemove: (index: number) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-center justify-between">
        <SectionBlockTitle title={title} status={status} />
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-admin"
        >
          <Plus className="w-3.5 h-3.5" /> Add Bracket
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {rows.map((row, index) => (
          <div key={`${title}-${index}`} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-3">
              <NumberField
                label="From Rank"
                value={row.fromRank}
                onChange={(value) => onChange(index, { fromRank: value })}
                min={1}
                disabled={disabled}
              />
            </div>
            <div className="col-span-3">
              <NumberField
                label="To Rank"
                value={row.toRank}
                onChange={(value) => onChange(index, { toRank: value })}
                min={1}
                disabled={disabled}
              />
            </div>
            <div className="col-span-4">
              <NumberField
                label="Cash"
                value={row.cashUsd}
                onChange={(value) => onChange(index, { cashUsd: value })}
                min={0}
                suffix="USD"
                disabled={disabled}
              />
            </div>
            <div className="col-span-2">
              <button
                type="button"
                onClick={() => onRemove(index)}
                disabled={disabled || rows.length <= 1}
                className="w-full rounded-xl border border-red-300/20 bg-red-400/5 px-2 py-2 text-red-200 hover:text-red-100 hover:border-red-200/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function TicketBracketEditor({
  title,
  status,
  rows,
  onChange,
  onAdd,
  onRemove,
  disabled,
}: {
  title: string
  status?: "active" | "not_active"
  rows: RankTicketBracket[]
  onChange: (index: number, patch: Partial<RankTicketBracket>) => void
  onAdd: () => void
  onRemove: (index: number) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-center justify-between">
        <SectionBlockTitle title={title} status={status} />
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-admin"
        >
          <Plus className="w-3.5 h-3.5" /> Add Bracket
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {rows.map((row, index) => (
          <div key={`${title}-${index}`} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-3">
              <NumberField
                label="From Rank"
                value={row.fromRank}
                onChange={(value) => onChange(index, { fromRank: value })}
                min={1}
                disabled={disabled}
              />
            </div>
            <div className="col-span-3">
              <NumberField
                label="To Rank"
                value={row.toRank}
                onChange={(value) => onChange(index, { toRank: value })}
                min={1}
                disabled={disabled}
              />
            </div>
            <div className="col-span-4">
              <NumberField
                label="Tickets"
                value={row.tickets}
                onChange={(value) => onChange(index, { tickets: value })}
                min={0}
                disabled={disabled}
              />
            </div>
            <div className="col-span-2">
              <button
                type="button"
                onClick={() => onRemove(index)}
                disabled={disabled || rows.length <= 1}
                className="w-full rounded-xl border border-red-300/20 bg-red-400/5 px-2 py-2 text-red-200 hover:text-red-100 hover:border-red-200/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function RafflePrizeEditor({
  title,
  status,
  rows,
  onChange,
  onAdd,
  onRemove,
  disabled,
}: {
  title: string
  status?: "active" | "not_active"
  rows: RafflePrize[]
  onChange: (index: number, patch: Partial<RafflePrize>) => void
  onAdd: () => void
  onRemove: (index: number) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-center justify-between">
        <SectionBlockTitle title={title} status={status} />
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-admin"
        >
          <Plus className="w-3.5 h-3.5" /> Add Prize
        </button>
      </div>

      <div className="mt-3 space-y-3">
        {rows.map((row, index) => (
          <div key={`${title}-${index}`} className="rounded-xl border border-white/10 bg-black/20 p-3">
            <div className="grid grid-cols-12 gap-2 items-end">
              <label className="col-span-4 space-y-1 block">
                <span className="text-xs uppercase tracking-wide text-white/45 font-admin">Label</span>
                <input
                  value={row.label}
                  disabled={disabled}
                  onChange={(event) => onChange(index, { label: event.target.value })}
                  className="w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-sm text-white font-admin outline-none focus:border-[#F37020] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </label>
              <div className="col-span-2">
                <NumberField
                  label="Winners"
                  value={row.winners}
                  onChange={(value) => onChange(index, { winners: value })}
                  min={1}
                  disabled={disabled}
                />
              </div>
              <div className="col-span-2">
                <NumberField
                  label="Fixed Cash"
                  value={row.cashUsd ?? 0}
                  onChange={(value) => onChange(index, { cashUsd: value })}
                  min={0}
                  disabled={disabled}
                />
              </div>
              <div className="col-span-2">
                <NumberField
                  label="Min Cash"
                  value={row.cashUsdMin ?? 0}
                  onChange={(value) => onChange(index, { cashUsdMin: value })}
                  min={0}
                  disabled={disabled}
                />
              </div>
              <div className="col-span-1">
                <NumberField
                  label="Max"
                  value={row.cashUsdMax ?? 0}
                  onChange={(value) => onChange(index, { cashUsdMax: value })}
                  min={0}
                  disabled={disabled}
                />
              </div>
              <div className="col-span-1">
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  disabled={disabled || rows.length <= 1}
                  className="w-full rounded-xl border border-red-300/20 bg-red-400/5 px-2 py-2 text-red-200 hover:text-red-100 hover:border-red-200/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4 mx-auto" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function NumberListEditor({
  title,
  status,
  values,
  onChange,
  onAdd,
  onRemove,
  disabled,
}: {
  title: string
  status?: "active" | "not_active"
  values: number[]
  onChange: (index: number, next: number) => void
  onAdd: () => void
  onRemove: (index: number) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-center justify-between">
        <SectionBlockTitle title={title} status={status} />
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-admin"
        >
          <Plus className="w-3.5 h-3.5" /> Add Value
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {values.map((value, index) => (
          <div key={`${title}-${index}`} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-10">
              <NumberField
                label={`Value ${index + 1}`}
                value={value}
                onChange={(next) => onChange(index, next)}
                disabled={disabled}
              />
            </div>
            <div className="col-span-2">
              <button
                type="button"
                onClick={() => onRemove(index)}
                disabled={disabled || values.length <= 1}
                className="w-full rounded-xl border border-red-300/20 bg-red-400/5 px-2 py-2 text-red-200 hover:text-red-100 hover:border-red-200/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function StringListEditor({
  title,
  status,
  values,
  onChange,
  onAdd,
  onRemove,
  disabled,
}: {
  title: string
  status?: "active" | "not_active"
  values: string[]
  onChange: (index: number, next: string) => void
  onAdd: () => void
  onRemove: (index: number) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <div className="flex items-center justify-between">
        <SectionBlockTitle title={title} status={status} />
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-admin"
        >
          <Plus className="w-3.5 h-3.5" /> Add Item
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {values.map((value, index) => (
          <div key={`${title}-${index}`} className="grid grid-cols-12 gap-2 items-center">
            <input
              value={value}
              disabled={disabled}
              onChange={(event) => onChange(index, event.target.value)}
              className="col-span-10 rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-sm text-white font-admin outline-none focus:border-[#F37020] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              type="button"
              onClick={() => onRemove(index)}
              disabled={disabled || values.length <= 1}
              className="col-span-2 rounded-xl border border-red-300/20 bg-red-400/5 px-2 py-2 text-red-200 hover:text-red-100 hover:border-red-200/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4 mx-auto" />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

function MinesCashoutEditor({
  title,
  status,
  rows,
  onChange,
  onAdd,
  onRemove,
  disabled,
}: {
  title: string
  status?: "active" | "not_active"
  rows: MinesCashoutRow[]
  onChange: (index: number, patch: Partial<MinesCashoutRow>) => void
  onAdd: () => void
  onRemove: (index: number) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.04] to-white/[0.01] overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between gap-4">
          <SectionBlockTitle title={title} status={status} />
          <button
            type="button"
            onClick={onAdd}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] border border-white/[0.08] px-4 py-2 text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/[0.1] hover:border-white/15 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed font-admin"
          >
            <Plus className="w-3.5 h-3.5" /> Add Step
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.04] bg-white/[0.015]">
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-6 py-3 w-[60px]">#</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3">Safe Picks</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3">Multiplier</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3">Survival Prob</th>
              <th className="w-[50px] px-3 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {rows.map((row, index) => (
              <tr
                key={`${title}-${index}`}
                className="group hover:bg-white/[0.02] transition-colors duration-150"
              >
                <td className="px-6 py-3">
                  <span className="text-[11px] text-white/25 font-admin tabular-nums">{index + 1}</span>
                </td>
                <td className="px-3 py-3">
                  <input
                    type="number"
                    value={row.safePicks}
                    min={1}
                    disabled={disabled}
                    onChange={(event) => onChange(index, { safePicks: asNumber(event.target.value, row.safePicks) })}
                    className="w-full min-w-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                  />
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      value={row.multiplier}
                      step={0.01}
                      disabled={disabled}
                      onChange={(event) => onChange(index, { multiplier: asNumber(event.target.value, row.multiplier) })}
                      className="w-full min-w-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                    />
                    <span className="text-[11px] text-white/30 font-admin shrink-0">x</span>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <input
                    type="number"
                    value={row.survivalProbability ?? 0}
                    step={0.0001}
                    disabled={disabled}
                    onChange={(event) => onChange(index, { survivalProbability: asNumber(event.target.value, row.survivalProbability ?? 0) })}
                    className="w-full min-w-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                  />
                </td>
                <td className="px-3 py-3">
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    disabled={disabled || rows.length <= 1}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white/20 hover:text-red-400 hover:bg-red-400/10 transition-all duration-200 disabled:opacity-0 disabled:cursor-not-allowed opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-white/[0.06] bg-white/[0.015]">
        <span className="text-[11px] text-white/30 font-admin">{rows.length} step{rows.length !== 1 ? "s" : ""} configured</span>
      </div>
    </div>
  )
}

function PrizeTypeTag({ type }: { type: LiveGamePrizeType }) {
  const config: Record<LiveGamePrizeType, { bg: string; text: string; label: string }> = {
    nothing: { bg: "bg-white/[0.06]", text: "text-white/50", label: "Nothing" },
    points: { bg: "bg-[#F37020]/15", text: "text-[#F9A86C]", label: "Points" },
    bonus: { bg: "bg-emerald-400/15", text: "text-emerald-300", label: "Bonus" },
    free_spins: { bg: "bg-purple-400/15", text: "text-purple-300", label: "Free Spins" },
    item: { bg: "bg-sky-400/15", text: "text-sky-300", label: "Item" },
    side_quest: { bg: "bg-yellow-400/15", text: "text-yellow-300", label: "Side Quest" },
  }
  const c = config[type] ?? config.nothing
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium tracking-wide ${c.bg} ${c.text} font-admin`}>
      {c.label}
    </span>
  )
}

function ProbabilityBar({ value, max = 100 }: { value: number; max?: number }) {
  const pct = Math.min((value / max) * 100, 100)
  return (
    <div className="h-1 w-full rounded-full bg-white/[0.06] overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-[#F37020] to-[#F9A86C] transition-all duration-300"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

function LiveGamePrizeTableEditor({
  title,
  description,
  status,
  rows,
  onChange,
  onAdd,
  onRemove,
  disabled,
}: {
  title: string
  description?: string
  status?: "active" | "not_active"
  rows: LiveGamePrizeConfig[]
  onChange: (index: number, patch: Partial<LiveGamePrizeConfig>) => void
  onAdd: () => void
  onRemove: (index: number) => void
  disabled?: boolean
}) {
  const totalPct = sumProbabilityRows(rows)
  const rewardTypeOptions: LiveGamePrizeType[] = ["nothing", "points", "bonus", "free_spins", "item", "side_quest"]

  return (
    <div className="rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.04] to-white/[0.01] overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div>
              <div className="flex items-center gap-2.5">
                <h4 className="text-[15px] font-semibold text-white/95 font-admin tracking-tight">{title}</h4>
                {status && <FeatureStatusBadge status={status} />}
              </div>
              {description && <p className="text-[12px] text-white/40 mt-1 font-admin leading-relaxed">{description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ProbabilityTotalBadge total={totalPct} />
            <button
              type="button"
              onClick={onAdd}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] border border-white/[0.08] px-4 py-2 text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/[0.1] hover:border-white/15 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed font-admin"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.04] bg-white/[0.015]">
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-6 py-3 w-[80px]">#</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3">Type</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3">Label</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3 w-[140px]">Amount</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3 w-[200px]">Probability</th>
              <th className="w-[50px] px-3 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {rows.map((row, index) => (
              <tr
                key={`${title}-${index}`}
                className="group hover:bg-white/[0.02] transition-colors duration-150"
              >
                {/* Row number */}
                <td className="px-6 py-3">
                  <span className="text-[11px] text-white/25 font-admin tabular-nums">{index + 1}</span>
                </td>

                {/* Type select */}
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <select
                      value={row.type}
                      onChange={(event) => onChange(index, { type: event.target.value as LiveGamePrizeType })}
                      disabled={disabled}
                      className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/85 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed appearance-none cursor-pointer"
                    >
                      {rewardTypeOptions.map((option) => (
                        <option key={option} value={option} className="bg-[#0F1219] text-white">
                          {option}
                        </option>
                      ))}
                    </select>
                    <PrizeTypeTag type={row.type} />
                  </div>
                </td>

                {/* Label */}
                <td className="px-3 py-3">
                  <input
                    value={row.label}
                    disabled={disabled}
                    onChange={(event) => onChange(index, { label: event.target.value })}
                    className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed placeholder:text-white/20"
                    placeholder="Prize label..."
                  />
                </td>

                {/* Amount */}
                <td className="px-3 py-3">
                  <input
                    type="number"
                    value={row.amount}
                    disabled={disabled}
                    onChange={(event) => onChange(index, { amount: asNumber(event.target.value, row.amount) })}
                    className="w-full min-w-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                  />
                </td>

                {/* Probability */}
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={row.probabilityPct}
                      step={0.001}
                      disabled={disabled}
                      onChange={(event) => onChange(index, { probabilityPct: asNumber(event.target.value, row.probabilityPct) })}
                      className="w-24 min-w-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                    />
                    <span className="text-[11px] text-white/30 font-admin shrink-0">%</span>
                    <div className="flex-1 min-w-[60px]">
                      <ProbabilityBar value={row.probabilityPct} />
                    </div>
                  </div>
                </td>

                {/* Delete */}
                <td className="px-3 py-3">
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    disabled={disabled || rows.length <= 1}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white/20 hover:text-red-400 hover:bg-red-400/10 transition-all duration-200 disabled:opacity-0 disabled:cursor-not-allowed opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer summary */}
      <div className="px-6 py-3 border-t border-white/[0.06] bg-white/[0.015] flex items-center justify-between">
        <span className="text-[11px] text-white/30 font-admin">{rows.length} prize{rows.length !== 1 ? "s" : ""} configured</span>
        <span className="text-[11px] text-white/30 font-admin tabular-nums">{totalPct.toFixed(3)}% allocated</span>
      </div>
    </div>
  )
}

function WelcomeSpinPrizeTableEditor({
  title,
  status,
  rows,
  onChange,
  onAdd,
  onRemove,
  disabled,
}: {
  title: string
  status?: "active" | "not_active"
  rows: WelcomeSpinPrizeConfig[]
  onChange: (index: number, patch: Partial<WelcomeSpinPrizeConfig>) => void
  onAdd: () => void
  onRemove: (index: number) => void
  disabled?: boolean
}) {
  const totalPct = sumProbabilityRows(rows)

  return (
    <div className="rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.04] to-white/[0.01] overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div>
              <div className="flex items-center gap-2.5">
                <h4 className="text-[15px] font-semibold text-white/95 font-admin tracking-tight">{title}</h4>
                {status && <FeatureStatusBadge status={status} />}
              </div>
              <p className="text-[12px] text-white/40 mt-1 font-admin leading-relaxed">Onboarding welcome cash slot prizes + odds.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ProbabilityTotalBadge total={totalPct} />
            <button
              type="button"
              onClick={onAdd}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] border border-white/[0.08] px-4 py-2 text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/[0.1] hover:border-white/15 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed font-admin"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.04] bg-white/[0.015]">
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-6 py-3 w-[80px]">#</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3 w-[160px]">Amount</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3">Label</th>
              <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3 w-[200px]">Probability</th>
              <th className="w-[50px] px-3 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {rows.map((row, index) => (
              <tr
                key={`${title}-${index}`}
                className="group hover:bg-white/[0.02] transition-colors duration-150"
              >
                {/* Row number */}
                <td className="px-6 py-3">
                  <span className="text-[11px] text-white/25 font-admin tabular-nums">{index + 1}</span>
                </td>

                {/* Amount */}
                <td className="px-3 py-3">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-white/30 font-admin">$</span>
                    <input
                      type="number"
                      value={row.amount}
                      min={0}
                      disabled={disabled}
                      onChange={(event) => onChange(index, { amount: asNumber(event.target.value, row.amount) })}
                      className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] pl-7 pr-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                    />
                  </div>
                </td>

                {/* Label */}
                <td className="px-3 py-3">
                  <input
                    value={row.label}
                    disabled={disabled}
                    onChange={(event) => onChange(index, { label: event.target.value })}
                    className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed placeholder:text-white/20"
                    placeholder="Prize label..."
                  />
                </td>

                {/* Probability */}
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={row.probabilityPct}
                      step={0.001}
                      disabled={disabled}
                      onChange={(event) => onChange(index, { probabilityPct: asNumber(event.target.value, row.probabilityPct) })}
                      className="w-24 min-w-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                    />
                    <span className="text-[11px] text-white/30 font-admin shrink-0">%</span>
                    <div className="flex-1 min-w-[60px]">
                      <ProbabilityBar value={row.probabilityPct} />
                    </div>
                  </div>
                </td>

                {/* Delete */}
                <td className="px-3 py-3">
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    disabled={disabled || rows.length <= 1}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white/20 hover:text-red-400 hover:bg-red-400/10 transition-all duration-200 disabled:opacity-0 disabled:cursor-not-allowed opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer summary */}
      <div className="px-6 py-3 border-t border-white/[0.06] bg-white/[0.015] flex items-center justify-between">
        <span className="text-[11px] text-white/30 font-admin">{rows.length} prize{rows.length !== 1 ? "s" : ""} configured</span>
        <span className="text-[11px] text-white/30 font-admin tabular-nums">{totalPct.toFixed(3)}% allocated</span>
      </div>
    </div>
  )
}

export function AdminEconomics({ permissions }: AdminEconomicsProps) {
  const { getAdminEconomicsSettings, updateAdminEconomicsSettings } = useAuth()
  const canEdit = Boolean(permissions?.canManageSystemSettings)

  const [settings, setSettings] = React.useState<OvalClubSettings>(() => cloneDefaultOvalClubSettings())
  const [baselineSettings, setBaselineSettings] = React.useState<OvalClubSettings>(() => cloneDefaultOvalClubSettings())
  const [activeSection, setActiveSection] = React.useState<SectionId>("foundation")

  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null)
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState<string | null>(null)
  const [lastUpdatedBy, setLastUpdatedBy] = React.useState<string | null>(null)

  const mutateSettings = React.useCallback((mutator: (next: OvalClubSettings) => void) => {
    setSettings((prev) => {
      const next = structuredClone(prev)
      mutator(next)
      return next
    })
  }, [])

  const hasChanges = React.useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(baselineSettings)
  }, [settings, baselineSettings])

  const loadSettings = React.useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    setStatusMessage(null)

    const result = await getAdminEconomicsSettings()
    if (!result.success || !result.settings) {
      setLoadError(result.message || "Failed to load economics settings.")
      setIsLoading(false)
      return
    }

    const normalized = normalizeOvalClubSettings(result.settings)
    setSettings(normalized)
    setBaselineSettings(normalized)
    setLastUpdatedAt(toDisplayString(result.updatedAt))
    setLastUpdatedBy(toDisplayString(result.updatedBy))
    setIsLoading(false)
  }, [getAdminEconomicsSettings])

  React.useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const handleResetToDefaults = () => {
    setSettings(cloneDefaultOvalClubSettings())
    setStatusMessage("Loaded defaults. Save changes to persist.")
  }

  const handleSave = async () => {
    if (!canEdit) return

    setIsSaving(true)
    setStatusMessage(null)

    const payload = normalizeOvalClubSettings(settings)
    const result = await updateAdminEconomicsSettings(payload)

    if (!result.success || !result.settings) {
      setStatusMessage(result.message || "Failed to save settings.")
      setIsSaving(false)
      return
    }

    const normalized = normalizeOvalClubSettings(result.settings)
    setSettings(normalized)
    setBaselineSettings(normalized)
    setLastUpdatedAt(toDisplayString(result.updatedAt))
    setLastUpdatedBy(toDisplayString(result.updatedBy))
    setStatusMessage("Economics settings saved.")
    setIsSaving(false)
  }

  if (isLoading) {
    return (
      <AdminCard className="bg-[radial-gradient(120%_120%_at_0%_0%,rgba(186,212,255,0.24),rgba(255,255,255,0.03))] border-white/15">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-[#9EC6FF]/40 border-t-[#9EC6FF] rounded-full animate-spin" />
          <p className="text-sm text-white/70 font-admin">Loading economics control center...</p>
        </div>
      </AdminCard>
    )
  }

  if (loadError) {
    return (
      <AdminCard className="bg-[radial-gradient(140%_130%_at_0%_0%,rgba(255,115,115,0.2),rgba(255,255,255,0.03))] border-red-300/25">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-300 mt-0.5" />
          <div>
            <p className="text-sm text-red-100 font-admin">{loadError}</p>
            <button
              onClick={loadSettings}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.05] border border-white/20 text-xs text-white/80 hover:text-white font-admin transition-colors"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        </div>
      </AdminCard>
    )
  }

  const derivedDailyBudget = settings.coreEconomy.defaultActiveReps * settings.coreEconomy.targetPointsPerRepPerDay

  return (
    <div className="space-y-6">
      <AdminCard className="bg-[radial-gradient(140%_140%_at_0%_0%,rgba(173,207,255,0.22),rgba(255,255,255,0.02))] border-white/15 backdrop-blur-xl">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="space-y-2">
            <h3 className="text-lg text-white font-semibold tracking-tight" style={{ fontFamily: "'SF Pro Display', 'Outfit', sans-serif" }}>
              Economics Control Center
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
              <div className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-white/40 font-admin">Daily Budget</p>
                <p className="text-sm text-white/90 font-mono-admin">{fmtCompact(settings.coreEconomy.systemDailyPointBudget)} pts</p>
              </div>
              <div className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-white/40 font-admin">Computed Budget</p>
                <p className="text-sm text-white/90 font-mono-admin">{fmtCompact(derivedDailyBudget)} pts</p>
              </div>
              <div className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-white/40 font-admin">Version</p>
                <p className="text-sm text-white/90 font-mono-admin">{settings.version}</p>
              </div>
              <div className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-white/40 font-admin">Pending Changes</p>
                <p className="text-sm text-white/90 font-mono-admin">{hasChanges ? "Yes" : "No"}</p>
              </div>
            </div>
            <div className="text-[11px] text-white/35 font-mono-admin">
              updated_at: {lastUpdatedAt || "n/a"} | updated_by: {lastUpdatedBy || "n/a"}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!canEdit && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-500/10 text-yellow-200 text-[11px] font-admin border border-yellow-300/20">
                <Lock className="w-3 h-3" />
                Read-only
              </span>
            )}
            <button
              onClick={loadSettings}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/15 text-xs text-white/80 hover:text-white transition-colors font-admin"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              Reload
            </button>
            <button
              onClick={handleResetToDefaults}
              disabled={!canEdit || isSaving}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/15 text-xs text-white/80 hover:text-white transition-colors disabled:opacity-45 disabled:cursor-not-allowed font-admin"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={!canEdit || isSaving || !hasChanges}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#9EC6FF] text-[#052143] text-xs font-semibold hover:bg-[#B4D5FF] transition-colors disabled:opacity-45 disabled:cursor-not-allowed font-admin"
            >
              <Save className="w-3.5 h-3.5" />
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {statusMessage && (
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-white/80 font-admin rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#9EC6FF]" />
            {statusMessage}
          </div>
        )}
      </AdminCard>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {sectionTabs.map((section) => (
          <SectionTabButton
            key={section.id}
            active={activeSection === section.id}
            icon={section.icon}
            label={section.label}
            onClick={() => setActiveSection(section.id)}
          />
        ))}
      </div>

      <SectionImpactCard section={activeSection} />
      <SectionRuntimeStatusCard section={activeSection} />

      {activeSection === "foundation" && (
        <div className="space-y-4">
          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Core Economy" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <NumberField label="Points per $1" value={settings.coreEconomy.pointsPerDollar} onChange={(value) => mutateSettings((next) => { next.coreEconomy.pointsPerDollar = value })} disabled={!canEdit} />
              <NumberField label="Target points / rep / day" value={settings.coreEconomy.targetPointsPerRepPerDay} onChange={(value) => mutateSettings((next) => { next.coreEconomy.targetPointsPerRepPerDay = value })} disabled={!canEdit} />
              <NumberField label="Default active reps" value={settings.coreEconomy.defaultActiveReps} onChange={(value) => mutateSettings((next) => { next.coreEconomy.defaultActiveReps = value })} disabled={!canEdit} />
              <NumberField label="System daily budget" value={settings.coreEconomy.systemDailyPointBudget} onChange={(value) => mutateSettings((next) => { next.coreEconomy.systemDailyPointBudget = value })} disabled={!canEdit} />
              <NumberField label="Season length" value={settings.coreEconomy.seasonLengthMonths} suffix="months" onChange={(value) => mutateSettings((next) => { next.coreEconomy.seasonLengthMonths = value })} disabled={!canEdit} />
              <NumberField label="Leaderboard cutoff" value={settings.coreEconomy.topLeaderboardCutoff} onChange={(value) => mutateSettings((next) => { next.coreEconomy.topLeaderboardCutoff = value })} disabled={!canEdit} />
              <NumberField label="Weekly reach min" value={settings.coreEconomy.weeklyTierReachPctMin} suffix="%" onChange={(value) => mutateSettings((next) => { next.coreEconomy.weeklyTierReachPctMin = value })} disabled={!canEdit} />
              <NumberField label="Weekly reach max" value={settings.coreEconomy.weeklyTierReachPctMax} suffix="%" onChange={(value) => mutateSettings((next) => { next.coreEconomy.weeklyTierReachPctMax = value })} disabled={!canEdit} />
              <NumberField label="EV cap of profit" value={settings.coreEconomy.maxCommunityEvPctOfProfit} suffix="%" onChange={(value) => mutateSettings((next) => { next.coreEconomy.maxCommunityEvPctOfProfit = value })} disabled={!canEdit} />
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <ToggleField label="Points reset each season" value={settings.coreEconomy.pointsResetEachSeason} onChange={(value) => mutateSettings((next) => { next.coreEconomy.pointsResetEachSeason = value })} disabled={!canEdit} />
              <ToggleField label="All-time stats persist" value={settings.coreEconomy.allTimeStatsNeverReset} onChange={(value) => mutateSettings((next) => { next.coreEconomy.allTimeStatsNeverReset = value })} disabled={!canEdit} />
              <button
                type="button"
                onClick={() => mutateSettings((next) => { next.coreEconomy.systemDailyPointBudget = next.coreEconomy.defaultActiveReps * next.coreEconomy.targetPointsPerRepPerDay })}
                disabled={!canEdit}
                className="rounded-2xl border border-[#9EC6FF]/40 bg-[#9EC6FF]/10 text-[#CDE2FF] hover:text-white hover:bg-[#9EC6FF]/20 transition-colors text-sm font-admin px-4 py-3 disabled:opacity-45 disabled:cursor-not-allowed"
              >
                Sync budget to reps × target
              </button>
            </div>
          </AdminCard>

          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Cadence and Cash Control" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ToggleField label="Weekly leaderboard" value={settings.eventCadence.weeklyLeaderboardEnabled} onChange={(value) => mutateSettings((next) => { next.eventCadence.weeklyLeaderboardEnabled = value })} disabled={!canEdit} />
              <ToggleField label="Monthly leaderboard" value={settings.eventCadence.monthlyLeaderboardEnabled} onChange={(value) => mutateSettings((next) => { next.eventCadence.monthlyLeaderboardEnabled = value })} disabled={!canEdit} />
              <ToggleField label="Seasonal leaderboard" value={settings.eventCadence.seasonalLeaderboardEnabled} onChange={(value) => mutateSettings((next) => { next.eventCadence.seasonalLeaderboardEnabled = value })} disabled={!canEdit} />
              <SelectField
                label="KOTH cadence"
                value={settings.eventCadence.kingOfTheHillCadence}
                options={["weekly", "monthly", "quarterly", "seasonal"]}
                onChange={(value) => mutateSettings((next) => { next.eventCadence.kingOfTheHillCadence = value })}
                disabled={!canEdit}
              />
              <SelectField
                label="KOTH can be limited to"
                value={settings.eventCadence.kingOfTheHillCanBeLimitedTo}
                options={["none", "quarterly", "seasonal"]}
                onChange={(value) => mutateSettings((next) => { next.eventCadence.kingOfTheHillCanBeLimitedTo = value })}
                disabled={!canEdit}
              />
              <NumberField label="Wheel free spins/day" value={settings.eventCadence.dailyWheelFreeSpinsPerDay} onChange={(value) => mutateSettings((next) => { next.eventCadence.dailyWheelFreeSpinsPerDay = value; next.games.wheel.freeSpinsPerDay = value })} disabled={!canEdit} />
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <ToggleField label="Check-in grants wheel spins" value={settings.eventCadence.checkInCanGrantExtraWheelSpins} onChange={(value) => mutateSettings((next) => { next.eventCadence.checkInCanGrantExtraWheelSpins = value; next.games.wheel.checkInCanGrantExtraSpin = value })} disabled={!canEdit} />
              <ToggleField label="Cash never equals points" value={settings.cashControl.cashNeverEqualsPoints} onChange={(value) => mutateSettings((next) => { next.cashControl.cashNeverEqualsPoints = value })} disabled={!canEdit} />
              <ToggleField label="Points are withdrawable" description="Should stay OFF per policy" value={!settings.cashControl.pointsAreNotWithdrawable} onChange={(value) => mutateSettings((next) => { next.cashControl.pointsAreNotWithdrawable = !value })} disabled={!canEdit} />
            </div>
          </AdminCard>
        </div>
      )}

      {activeSection === "rewards" && (
        <div className="space-y-4">
          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Tier Unlock Ladders" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
              {seasonPeriods.map((period) => (
                <TierRowsEditor
                  key={period}
                  title={`${periodLabel(period)} tier unlocks`}
                  status="not_active"
                  rows={settings.tierEligibility[period]}
                  onChange={(index, patch) => mutateSettings((next) => {
                    const current = next.tierEligibility[period][index] ?? { points: 0, tickets: 0 }
                    next.tierEligibility[period][index] = { ...current, ...patch }
                  })}
                  onAdd={() => mutateSettings((next) => { next.tierEligibility[period].push({ points: 0, tickets: 0 }) })}
                  onRemove={(index) => mutateSettings((next) => { next.tierEligibility[period] = next.tierEligibility[period].filter((_, i) => i !== index) })}
                  disabled={!canEdit}
                />
              ))}
            </div>
          </AdminCard>

          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Individual Leaderboards" status="active" className="mb-4" />
            <div className="space-y-4">
              {seasonPeriods.map((period) => {
                const board = settings.leaderboards.individual[period]
                return (
                  <div key={`individual-${period}`} className="rounded-2xl border border-white/10 bg-black/20 p-4 space-y-3">
                    <SectionBlockTitle
                      title={`${periodLabel(period)} Individual Leaderboard`}
                      status={period === "weekly" ? "active" : "not_active"}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <NumberField
                        label={`${periodLabel(period)} top cutoff`}
                        value={board.topCutoff}
                        onChange={(value) => mutateSettings((next) => { next.leaderboards.individual[period].topCutoff = value })}
                        disabled={!canEdit}
                      />
                      <NumberField
                        label="Raffle pot min"
                        value={board.rafflePotUsdMin}
                        onChange={(value) => mutateSettings((next) => { next.leaderboards.individual[period].rafflePotUsdMin = value })}
                        disabled={!canEdit}
                      />
                      <NumberField
                        label="Raffle pot max"
                        value={board.rafflePotUsdMax}
                        onChange={(value) => mutateSettings((next) => { next.leaderboards.individual[period].rafflePotUsdMax = value })}
                        disabled={!canEdit}
                      />
                    </div>
                    <CashBracketEditor
                      title={`${periodLabel(period)} guaranteed cash`}
                      status="not_active"
                      rows={board.guaranteedCashBrackets}
                      onChange={(index, patch) => mutateSettings((next) => {
                        const current = next.leaderboards.individual[period].guaranteedCashBrackets[index] ?? {
                          fromRank: 1,
                          toRank: 1,
                          cashUsd: 0,
                        }
                        next.leaderboards.individual[period].guaranteedCashBrackets[index] = {
                          ...current,
                          ...patch,
                        }
                      })}
                      onAdd={() => mutateSettings((next) => { next.leaderboards.individual[period].guaranteedCashBrackets.push({ fromRank: 1, toRank: 1, cashUsd: 0 }) })}
                      onRemove={(index) => mutateSettings((next) => {
                        next.leaderboards.individual[period].guaranteedCashBrackets = next.leaderboards.individual[period].guaranteedCashBrackets.filter((_, i) => i !== index)
                      })}
                      disabled={!canEdit}
                    />
                    <TicketBracketEditor
                      title={`${periodLabel(period)} placement tickets`}
                      status="not_active"
                      rows={board.placementTicketBrackets}
                      onChange={(index, patch) => mutateSettings((next) => {
                        const current = next.leaderboards.individual[period].placementTicketBrackets[index] ?? {
                          fromRank: 1,
                          toRank: 1,
                          tickets: 0,
                        }
                        next.leaderboards.individual[period].placementTicketBrackets[index] = {
                          ...current,
                          ...patch,
                        }
                      })}
                      onAdd={() => mutateSettings((next) => { next.leaderboards.individual[period].placementTicketBrackets.push({ fromRank: 1, toRank: 1, tickets: 0 }) })}
                      onRemove={(index) => mutateSettings((next) => {
                        next.leaderboards.individual[period].placementTicketBrackets = next.leaderboards.individual[period].placementTicketBrackets.filter((_, i) => i !== index)
                      })}
                      disabled={!canEdit}
                    />
                    <RafflePrizeEditor
                      title={`${periodLabel(period)} raffle prizes`}
                      status={period === "weekly" ? "active" : "not_active"}
                      rows={board.rafflePrizes.length > 0 ? board.rafflePrizes : [{ label: `${period}-prize`, winners: 1, cashUsd: 0 }]}
                      onChange={(index, patch) => mutateSettings((next) => {
                        const list = next.leaderboards.individual[period].rafflePrizes
                        if (!list.length) {
                          list.push({ label: `${period}-prize`, winners: 1, cashUsd: 0 })
                        }
                        const current = list[index] ?? { label: `${period}-prize`, winners: 1, cashUsd: 0 }
                        list[index] = { ...current, ...patch }
                      })}
                      onAdd={() => mutateSettings((next) => { next.leaderboards.individual[period].rafflePrizes.push({ label: `${period}-prize`, winners: 1, cashUsd: 0 }) })}
                      onRemove={(index) => mutateSettings((next) => {
                        next.leaderboards.individual[period].rafflePrizes = next.leaderboards.individual[period].rafflePrizes.filter((_, i) => i !== index)
                      })}
                      disabled={!canEdit}
                    />
                  </div>
                )
              })}
            </div>
          </AdminCard>

          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Team Leaderboards" status="not_active" className="mb-4" />
            <div className="space-y-4">
              {seasonPeriods.map((period) => {
                const board = settings.leaderboards.team[period]
                return (
                  <div key={`team-${period}`} className="rounded-2xl border border-white/10 bg-black/20 p-4 space-y-3">
                    <SectionBlockTitle title={`${periodLabel(period)} Team Leaderboard`} status="not_active" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <NumberField
                        label={`${periodLabel(period)} top cutoff`}
                        value={board.topCutoff}
                        onChange={(value) => mutateSettings((next) => { next.leaderboards.team[period].topCutoff = value })}
                        disabled={!canEdit}
                      />
                      {period === "weekly" && (
                        <ToggleField
                          label="Shared raffle tickets"
                          value={settings.leaderboards.team.weekly.sharedRaffleTicketsEnabled}
                          onChange={(value) => mutateSettings((next) => { next.leaderboards.team.weekly.sharedRaffleTicketsEnabled = value })}
                          disabled={!canEdit}
                        />
                      )}
                      {period === "seasonal" && (
                        <ToggleField
                          label="Team-only mega raffle"
                          value={settings.leaderboards.team.seasonal.teamOnlyMegaRaffleEnabled}
                          onChange={(value) => mutateSettings((next) => { next.leaderboards.team.seasonal.teamOnlyMegaRaffleEnabled = value })}
                          disabled={!canEdit}
                        />
                      )}
                    </div>
                    <CashBracketEditor
                      title={`${periodLabel(period)} team guaranteed cash`}
                      status="not_active"
                      rows={board.guaranteedCashBrackets}
                      onChange={(index, patch) => mutateSettings((next) => {
                        const current = next.leaderboards.team[period].guaranteedCashBrackets[index] ?? {
                          fromRank: 1,
                          toRank: 1,
                          cashUsd: 0,
                        }
                        next.leaderboards.team[period].guaranteedCashBrackets[index] = {
                          ...current,
                          ...patch,
                        }
                      })}
                      onAdd={() => mutateSettings((next) => { next.leaderboards.team[period].guaranteedCashBrackets.push({ fromRank: 1, toRank: 1, cashUsd: 0 }) })}
                      onRemove={(index) => mutateSettings((next) => {
                        next.leaderboards.team[period].guaranteedCashBrackets = next.leaderboards.team[period].guaranteedCashBrackets.filter((_, i) => i !== index)
                      })}
                      disabled={!canEdit}
                    />
                  </div>
                )
              })}
            </div>
          </AdminCard>
        </div>
      )}

      {activeSection === "qualification" && (
        <div className="space-y-4">
          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Individual Minimums" status="active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {seasonPeriods.map((period) => (
                <div key={`individual-min-${period}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
                  <SectionBlockTitle title={periodLabel(period)} status={period === "weekly" ? "active" : "not_active"} />
                  <NumberField
                    label="Minimum deals"
                    value={settings.qualificationGates.individual[period].minDeals}
                    onChange={(value) => mutateSettings((next) => { next.qualificationGates.individual[period].minDeals = value })}
                    disabled={!canEdit}
                  />
                  <NumberField
                    label="Minimum points"
                    value={settings.qualificationGates.individual[period].minPoints}
                    onChange={(value) => mutateSettings((next) => { next.qualificationGates.individual[period].minPoints = value })}
                    disabled={!canEdit}
                  />
                </div>
              ))}
            </div>
          </AdminCard>

          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Team Minimums" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {seasonPeriods.map((period) => (
                <div key={`team-min-${period}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
                  <SectionBlockTitle title={periodLabel(period)} status="not_active" />
                  <NumberField
                    label="Members meeting deals"
                    value={settings.qualificationGates.team[period].minMembersMeetingDealsPct}
                    suffix="%"
                    onChange={(value) => mutateSettings((next) => { next.qualificationGates.team[period].minMembersMeetingDealsPct = value })}
                    disabled={!canEdit}
                  />
                  <NumberField
                    label="Deals per qualified member"
                    value={settings.qualificationGates.team[period].minDealsPerQualifiedMember}
                    onChange={(value) => mutateSettings((next) => { next.qualificationGates.team[period].minDealsPerQualifiedMember = value })}
                    disabled={!canEdit}
                  />
                  <NumberField
                    label="Team deals per member"
                    value={settings.qualificationGates.team[period].minTeamDealsPerMember}
                    onChange={(value) => mutateSettings((next) => { next.qualificationGates.team[period].minTeamDealsPerMember = value })}
                    disabled={!canEdit}
                  />
                </div>
              ))}
            </div>
          </AdminCard>

          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Eligibility and UX Language" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ToggleField
                label="Weekly raffle requires minimums"
                value={settings.qualificationGates.raffleEligibility.weeklyRequiresIndividualMinimums}
                onChange={(value) => mutateSettings((next) => { next.qualificationGates.raffleEligibility.weeklyRequiresIndividualMinimums = value })}
                disabled={!canEdit}
              />
              <ToggleField
                label="Seasonal raffle requires minimums"
                value={settings.qualificationGates.raffleEligibility.seasonalRequiresIndividualMinimums}
                onChange={(value) => mutateSettings((next) => { next.qualificationGates.raffleEligibility.seasonalRequiresIndividualMinimums = value })}
                disabled={!canEdit}
              />
              <ToggleField
                label="No rewards without qualification"
                value={settings.qualificationGates.raffleEligibility.placementWithoutQualificationGetsNoRewards}
                onChange={(value) => mutateSettings((next) => { next.qualificationGates.raffleEligibility.placementWithoutQualificationGetsNoRewards = value })}
                disabled={!canEdit}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-3">
              <StringListEditor
                title="Positive qualification messages"
                status="not_active"
                values={settings.uiLanguage.qualificationMessages}
                onChange={(index, value) => mutateSettings((next) => { next.uiLanguage.qualificationMessages[index] = value })}
                onAdd={() => mutateSettings((next) => { next.uiLanguage.qualificationMessages.push("New message") })}
                onRemove={(index) => mutateSettings((next) => { next.uiLanguage.qualificationMessages = next.uiLanguage.qualificationMessages.filter((_, i) => i !== index) })}
                disabled={!canEdit}
              />
              <StringListEditor
                title="Avoid terms"
                status="not_active"
                values={settings.uiLanguage.avoidTerms}
                onChange={(index, value) => mutateSettings((next) => { next.uiLanguage.avoidTerms[index] = value })}
                onAdd={() => mutateSettings((next) => { next.uiLanguage.avoidTerms.push("term") })}
                onRemove={(index) => mutateSettings((next) => { next.uiLanguage.avoidTerms = next.uiLanguage.avoidTerms.filter((_, i) => i !== index) })}
                disabled={!canEdit}
              />
            </div>
          </AdminCard>
        </div>
      )}

      {activeSection === "events" && (
        <div className="space-y-4">
          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="King of the Hill Rules" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ToggleField label="KOTH enabled" value={settings.kingOfTheHill.enabled} onChange={(value) => mutateSettings((next) => { next.kingOfTheHill.enabled = value })} disabled={!canEdit} />
              <ToggleField label="Teams only" value={settings.kingOfTheHill.teamsOnly} onChange={(value) => mutateSettings((next) => { next.kingOfTheHill.teamsOnly = value })} disabled={!canEdit} />
              <ToggleField label="Weekly reset points" value={settings.kingOfTheHill.weeklyResetPoints} onChange={(value) => mutateSettings((next) => { next.kingOfTheHill.weeklyResetPoints = value })} disabled={!canEdit} />
              <ToggleField label="No carryover points" value={settings.kingOfTheHill.noCarryoverPoints} onChange={(value) => mutateSettings((next) => { next.kingOfTheHill.noCarryoverPoints = value })} disabled={!canEdit} />
              <NumberField label="Consecutive weeks required" value={settings.kingOfTheHill.requiredConsecutiveWeeksAtRankOne} onChange={(value) => mutateSettings((next) => { next.kingOfTheHill.requiredConsecutiveWeeksAtRankOne = value })} disabled={!canEdit} />
              <NumberField label="Dethroned challenger defense weeks" value={settings.kingOfTheHill.dethronedChallengerMustDefendWeeks} onChange={(value) => mutateSettings((next) => { next.kingOfTheHill.dethronedChallengerMustDefendWeeks = value })} disabled={!canEdit} />
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <NumberField label="Minimum active members" value={settings.qualificationGates.kingOfTheHill.minActiveMembersPct} suffix="%" onChange={(value) => mutateSettings((next) => { next.qualificationGates.kingOfTheHill.minActiveMembersPct = value })} disabled={!canEdit} />
              <NumberField label="Minimum deals/member" value={settings.qualificationGates.kingOfTheHill.minTeamDealsPerMember} onChange={(value) => mutateSettings((next) => { next.qualificationGates.kingOfTheHill.minTeamDealsPerMember = value })} disabled={!canEdit} />
              <NumberField label="Runner-up cash" value={settings.kingOfTheHill.runnerUpConsolation.cashUsd} onChange={(value) => mutateSettings((next) => { next.kingOfTheHill.runnerUpConsolation.cashUsd = value })} disabled={!canEdit} />
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <ToggleField label="Repeat minimums on defense" value={settings.qualificationGates.kingOfTheHill.mustRepeatMinimumsInDefenseWeek} onChange={(value) => mutateSettings((next) => { next.qualificationGates.kingOfTheHill.mustRepeatMinimumsInDefenseWeek = value })} disabled={!canEdit} />
              <ToggleField label="Must finish #1 both weeks" value={settings.qualificationGates.kingOfTheHill.mustFinishRankOneBothWeeks} onChange={(value) => mutateSettings((next) => { next.qualificationGates.kingOfTheHill.mustFinishRankOneBothWeeks = value })} disabled={!canEdit} />
              <ToggleField label="Unclaimed if no qualified team" value={settings.qualificationGates.kingOfTheHill.unclaimedIfNoQualifiedTeam} onChange={(value) => mutateSettings((next) => { next.qualificationGates.kingOfTheHill.unclaimedIfNoQualifiedTeam = value })} disabled={!canEdit} />
            </div>
          </AdminCard>

          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Champion Prize Options" status="not_active" className="mb-4" />
            <div className="space-y-3">
              {settings.kingOfTheHill.championPrizeOptions.map((option, index) => (
                <div key={`champion-option-${index}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <label className="col-span-4 space-y-1 block">
                      <span className="text-xs uppercase tracking-wide text-white/45 font-admin">Option label</span>
                      <input
                        value={option.label}
                        disabled={!canEdit}
                        onChange={(event) => mutateSettings((next) => {
                          const current = next.kingOfTheHill.championPrizeOptions[index]
                          if (!current) return
                          current.label = event.target.value
                        })}
                        className="w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-sm text-white font-admin outline-none focus:border-[#F37020] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </label>
                    <div className="col-span-2">
                      <NumberField label="Cash" value={option.cashUsd ?? 0} onChange={(value) => mutateSettings((next) => {
                        const current = next.kingOfTheHill.championPrizeOptions[index]
                        if (!current) return
                        current.cashUsd = value
                      })} disabled={!canEdit} />
                    </div>
                    <div className="col-span-3">
                      <NumberField label="Retail value" value={option.retailValueUsd ?? 0} onChange={(value) => mutateSettings((next) => {
                        const current = next.kingOfTheHill.championPrizeOptions[index]
                        if (!current) return
                        current.retailValueUsd = value
                      })} disabled={!canEdit} />
                    </div>
                    <div className="col-span-2">
                      <NumberField label="Est. cost" value={option.estimatedCostUsd ?? 0} onChange={(value) => mutateSettings((next) => {
                        const current = next.kingOfTheHill.championPrizeOptions[index]
                        if (!current) return
                        current.estimatedCostUsd = value
                      })} disabled={!canEdit} />
                    </div>
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => mutateSettings((next) => {
                          next.kingOfTheHill.championPrizeOptions = next.kingOfTheHill.championPrizeOptions.filter((_, i) => i !== index)
                        })}
                        disabled={!canEdit || settings.kingOfTheHill.championPrizeOptions.length <= 1}
                        className="w-full rounded-xl border border-red-300/20 bg-red-400/5 px-2 py-2 text-red-200 hover:text-red-100 hover:border-red-200/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => mutateSettings((next) => {
                  next.kingOfTheHill.championPrizeOptions.push({ label: "new-option", cashUsd: 0 })
                })}
                disabled={!canEdit}
                className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-admin"
              >
                <Plus className="w-3.5 h-3.5" /> Add Champion Option
              </button>
            </div>
          </AdminCard>
        </div>
      )}

      {activeSection === "games" && (
        <div className="space-y-4">
          <AdminCard className="bg-white/[0.03] border-white/10">
            <h4 className="text-sm text-white/90 font-admin mb-2">Frontend Live Connection Status</h4>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 text-xs font-admin">
              <div className="rounded-xl border border-emerald-300/20 bg-emerald-400/5 p-3">
                <div className="flex items-center gap-2">
                  <p className="text-emerald-100 font-semibold">Live now (frontend/gameplay connected)</p>
                  <FeatureStatusBadge status="active" />
                </div>
                <ul className="mt-2 space-y-1 text-white/70 leading-relaxed">
                  <li>Deal slot reward prizes + percentages (server-side reward roll)</li>
                  <li>Free spin reward prizes + percentages (server-side reward roll)</li>
                  <li>Welcome spin prizes + percentages (server-side roll + frontend display ladder)</li>
                  <li>Slot probability table (`games.slot.probabilities`) drives Deals tab &quot;Casino Slots&quot; mini-game odds</li>
                </ul>
              </div>
              <div className="rounded-xl border border-yellow-300/20 bg-yellow-400/5 p-3">
                <div className="flex items-center gap-2">
                  <p className="text-yellow-100 font-semibold">Stored config (not all runtime consumers confirmed)</p>
                  <FeatureStatusBadge status="not_active" />
                </div>
                <ul className="mt-2 space-y-1 text-white/70 leading-relaxed">
                  <li>Wheel outcomes (`games.wheel.outcomes`) are saved and editable but not wired to a runtime wheel reward selector yet.</li>
                  <li>Mines tables and throttle controls are persisted economics settings and may require specific runtime logic/thresholds to observe live effect.</li>
                  <li>Use this panel as the source of truth for what is currently wired live to frontend gameplay.</li>
                </ul>
              </div>
            </div>
          </AdminCard>


          {/* Simple Toggles Section */}
          <SimpleToggles
            outcomeTypes={[]}
            outcomeRanges={getDefaultOutcomeRanges()}
            onOutcomeTypesChange={() => {}}
            onOutcomeRangesChange={() => {}}
            disabled={!canEdit}
          />
          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Slot Game" status="active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <NumberField label="Expected return" value={settings.games.slot.expectedReturnMultiplier} step={0.01} onChange={(value) => mutateSettings((next) => { next.games.slot.expectedReturnMultiplier = value })} disabled={!canEdit} />
              <NumberField label="House edge" value={settings.games.slot.houseEdgePct} suffix="%" onChange={(value) => mutateSettings((next) => { next.games.slot.houseEdgePct = value })} disabled={!canEdit} />
              <NumberField label="Current wagers count" value={settings.games.slot.wagers.length} onChange={() => {}} disabled />
            </div>
            <div className="mt-3 grid grid-cols-1 xl:grid-cols-2 gap-3">
              <NumberListEditor
                title="Slot wagers"
                status="not_active"
                values={settings.games.slot.wagers}
                onChange={(index, value) => mutateSettings((next) => { next.games.slot.wagers[index] = value })}
                onAdd={() => mutateSettings((next) => { next.games.slot.wagers.push(0) })}
                onRemove={(index) => mutateSettings((next) => { next.games.slot.wagers = next.games.slot.wagers.filter((_, i) => i !== index) })}
                disabled={!canEdit}
              />
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm text-white/90 font-admin">Slot probability table</h4>
                    <FeatureStatusBadge status="active" />
                    <ProbabilityTotalBadge total={sumProbabilityRows(settings.games.slot.probabilities)} />
                  </div>
                  <button
                    type="button"
                    onClick={() => mutateSettings((next) => {
                      next.games.slot.probabilities.push({ outcome: "new", payoutMultiplier: 0, probabilityPct: 0 })
                    })}
                    disabled={!canEdit}
                    className="inline-flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/80 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-admin"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Outcome
                  </button>
                </div>
                <div className="mt-3 space-y-2">
                  {settings.games.slot.probabilities.map((row, index) => (
                    <div key={`slot-prob-${index}`} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        value={row.outcome}
                        disabled={!canEdit}
                        onChange={(event) => mutateSettings((next) => {
                          const current = next.games.slot.probabilities[index]
                          if (!current) return
                          current.outcome = event.target.value
                        })}
                        className="col-span-4 rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-sm text-white font-admin outline-none focus:border-[#F37020] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    <div className="col-span-3">
                        <NumberField label="Payout" value={row.payoutMultiplier} step={0.001} onChange={(value) => mutateSettings((next) => {
                          const current = next.games.slot.probabilities[index]
                          if (!current) return
                          current.payoutMultiplier = value
                        })} disabled={!canEdit} />
                      </div>
                      <div className="col-span-3">
                        <NumberField label="Probability" value={row.probabilityPct} step={0.001} suffix="%" onChange={(value) => mutateSettings((next) => {
                          const current = next.games.slot.probabilities[index]
                          if (!current) return
                          current.probabilityPct = value
                        })} disabled={!canEdit} />
                      </div>
                      <button
                        type="button"
                        onClick={() => mutateSettings((next) => { next.games.slot.probabilities = next.games.slot.probabilities.filter((_, i) => i !== index) })}
                        disabled={!canEdit || settings.games.slot.probabilities.length <= 1}
                        className="col-span-2 rounded-xl border border-red-300/20 bg-red-400/5 px-2 py-2 text-red-200 hover:text-red-100 hover:border-red-200/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AdminCard>

          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Mines and Wheel" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <NumberField label="Mines grid tiles" value={settings.games.mines.gridTiles} onChange={(value) => mutateSettings((next) => { next.games.mines.gridTiles = value })} disabled={!canEdit} />
              <NumberField label="Mines house edge" value={settings.games.mines.houseEdgePct} suffix="%" onChange={(value) => mutateSettings((next) => { next.games.mines.houseEdgePct = value })} disabled={!canEdit} />
              <SelectField
                label="Default mines mode"
                value={settings.games.mines.defaultMode}
                options={["easy", "normal", "hard"]}
                onChange={(value) => mutateSettings((next) => { next.games.mines.defaultMode = value })}
                disabled={!canEdit}
              />
              <NumberField label="Wheel free spins/day" value={settings.games.wheel.freeSpinsPerDay} onChange={(value) => mutateSettings((next) => { next.games.wheel.freeSpinsPerDay = value; next.eventCadence.dailyWheelFreeSpinsPerDay = value })} disabled={!canEdit} />
            </div>

            <div className="mt-4 flex flex-col gap-5">
              {(["easy", "normal", "hard"] as const).map((mode) => (
                <div key={`mines-mode-${mode}`} className="space-y-3">
                  <NumberField
                    label={`${mode} mode mine count`}
                    value={settings.games.mines.modes[mode].mines}
                    onChange={(value) => mutateSettings((next) => { next.games.mines.modes[mode].mines = value })}
                    disabled={!canEdit}
                  />
                  <MinesCashoutEditor
                    title={`${mode} cashout table`}
                    status="not_active"
                    rows={settings.games.mines.modes[mode].cashoutTable}
                    onChange={(index, patch) => mutateSettings((next) => {
                      const current = next.games.mines.modes[mode].cashoutTable[index] ?? { safePicks: 1, multiplier: 1 }
                      next.games.mines.modes[mode].cashoutTable[index] = {
                        ...current,
                        ...patch,
                      }
                    })}
                    onAdd={() => mutateSettings((next) => { next.games.mines.modes[mode].cashoutTable.push({ safePicks: 1, multiplier: 1 }) })}
                    onRemove={(index) => mutateSettings((next) => {
                      next.games.mines.modes[mode].cashoutTable = next.games.mines.modes[mode].cashoutTable.filter((_, i) => i !== index)
                    })}
                    disabled={!canEdit}
                  />
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.04] to-white/[0.01] overflow-hidden">
              {/* Header */}
              <div className="px-6 pt-5 pb-4 border-b border-white/[0.06]">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5">
                    <h4 className="text-[15px] font-semibold text-white/95 font-admin tracking-tight">Wheel outcomes</h4>
                    <FeatureStatusBadge status="not_active" />
                    <ProbabilityTotalBadge total={sumProbabilityRows(settings.games.wheel.outcomes)} />
                  </div>
                  <button
                    type="button"
                    onClick={() => mutateSettings((next) => {
                      next.games.wheel.outcomes.push({ outcome: "new", rewardType: "points", points: 0, probabilityPct: 0 })
                    })}
                    disabled={!canEdit}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] border border-white/[0.08] px-4 py-2 text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/[0.1] hover:border-white/15 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed font-admin"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Outcome
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.04] bg-white/[0.015]">
                      <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-6 py-3 w-[60px]">#</th>
                      <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3">Outcome</th>
                      <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3 w-[140px]">Reward</th>
                      <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3 w-[130px]">Points</th>
                      <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3 w-[130px]">Token x</th>
                      <th className="text-left text-[10px] uppercase tracking-widest text-white/30 font-admin font-medium px-3 py-3 w-[180px]">Probability</th>
                      <th className="w-[50px] px-3 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {settings.games.wheel.outcomes.map((row, index) => (
                      <tr
                        key={`wheel-outcome-${index}`}
                        className="group hover:bg-white/[0.02] transition-colors duration-150"
                      >
                        {/* Row number */}
                        <td className="px-6 py-3">
                          <span className="text-[11px] text-white/25 font-admin tabular-nums">{index + 1}</span>
                        </td>

                        {/* Outcome */}
                        <td className="px-3 py-3">
                          <input
                            value={row.outcome}
                            disabled={!canEdit}
                            onChange={(event) => mutateSettings((next) => {
                              const current = next.games.wheel.outcomes[index]
                              if (!current) return
                              current.outcome = event.target.value
                            })}
                            className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </td>

                        {/* Reward type */}
                        <td className="px-3 py-3">
                          <select
                            value={row.rewardType}
                            onChange={(event) => mutateSettings((next) => {
                              const current = next.games.wheel.outcomes[index]
                              if (!current) return
                              current.rewardType = event.target.value as "points" | "token"
                            })}
                            disabled={!canEdit}
                            className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/85 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed appearance-none cursor-pointer"
                          >
                            <option value="points" className="bg-[#0F1219] text-white">points</option>
                            <option value="token" className="bg-[#0F1219] text-white">token</option>
                          </select>
                        </td>

                        {/* Points */}
                        <td className="px-3 py-3">
                          <input
                            type="number"
                            value={row.points ?? 0}
                            disabled={!canEdit}
                            onChange={(event) => mutateSettings((next) => {
                              const current = next.games.wheel.outcomes[index]
                              if (!current) return
                              current.points = asNumber(event.target.value, row.points ?? 0)
                            })}
                            className="w-full min-w-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                          />
                        </td>

                        {/* Token multiplier */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              value={row.tokenMultiplier ?? 0}
                              step={0.1}
                              disabled={!canEdit}
                              onChange={(event) => mutateSettings((next) => {
                                const current = next.games.wheel.outcomes[index]
                                if (!current) return
                                current.tokenMultiplier = asNumber(event.target.value, row.tokenMultiplier ?? 0)
                              })}
                              className="w-full min-w-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                            />
                            <span className="text-[11px] text-white/30 font-admin shrink-0">x</span>
                          </div>
                        </td>

                        {/* Probability */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={row.probabilityPct}
                              step={0.001}
                              disabled={!canEdit}
                              onChange={(event) => mutateSettings((next) => {
                                const current = next.games.wheel.outcomes[index]
                                if (!current) return
                                current.probabilityPct = asNumber(event.target.value, row.probabilityPct)
                              })}
                              className="w-24 min-w-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-[13px] text-white/90 font-admin outline-none focus:border-[#F37020]/60 focus:bg-white/[0.05] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed tabular-nums"
                            />
                            <span className="text-[11px] text-white/30 font-admin shrink-0">%</span>
                          </div>
                        </td>

                        {/* Delete */}
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => mutateSettings((next) => { next.games.wheel.outcomes = next.games.wheel.outcomes.filter((_, i) => i !== index) })}
                            disabled={!canEdit || settings.games.wheel.outcomes.length <= 1}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/20 hover:text-red-400 hover:bg-red-400/10 transition-all duration-200 disabled:opacity-0 disabled:cursor-not-allowed opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-white/[0.06] bg-white/[0.015] flex items-center justify-between">
                <span className="text-[11px] text-white/30 font-admin">{settings.games.wheel.outcomes.length} outcome{settings.games.wheel.outcomes.length !== 1 ? "s" : ""} configured</span>
                <span className="text-[11px] text-white/30 font-admin tabular-nums">{sumProbabilityRows(settings.games.wheel.outcomes).toFixed(3)}% allocated</span>
              </div>
            </div>
          </AdminCard>

          <AdminCard className="bg-white/[0.02] border-white/[0.07]">
            <div className="mb-1.5 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-[#F37020]/10 border border-[#F37020]/20 flex items-center justify-center shrink-0">
                <CircleDollarSign className="w-3.5 h-3.5 text-[#F9A86C]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-white/95 font-admin tracking-tight">Live Reward Prize Tables</h4>
                  <FeatureStatusBadge status="active" />
                </div>
                <p className="text-[11px] text-white/40 font-admin mt-0.5">
                  Server-side prize rolls for deal slots, free spins, and welcome onboarding.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-6 mt-5">
              <LiveGamePrizeTableEditor
                title="Deal slot rewards"
                status="active"
                description="Admin-configurable prizes + odds for deal slot claims."
                rows={settings.games.slot.dealRewardPrizes}
                onChange={(index, patch) => mutateSettings((next) => {
                  const current = next.games.slot.dealRewardPrizes[index] ?? {
                    type: "nothing",
                    amount: 0,
                    label: "No reward",
                    probabilityPct: 0,
                  }
                  next.games.slot.dealRewardPrizes[index] = {
                    ...current,
                    ...patch,
                  }
                })}
                onAdd={() => mutateSettings((next) => {
                  next.games.slot.dealRewardPrizes.push({
                    type: "points",
                    amount: 10,
                    label: "New Prize",
                    probabilityPct: 0,
                  })
                })}
                onRemove={(index) => mutateSettings((next) => {
                  next.games.slot.dealRewardPrizes = next.games.slot.dealRewardPrizes.filter((_, i) => i !== index)
                })}
                disabled={!canEdit}
              />
              <LiveGamePrizeTableEditor
                title="Free spin rewards"
                status="active"
                description="Admin-configurable prizes + odds for using stored free spins."
                rows={settings.games.wheel.freeSpinRewardPrizes}
                onChange={(index, patch) => mutateSettings((next) => {
                  const current = next.games.wheel.freeSpinRewardPrizes[index] ?? {
                    type: "nothing",
                    amount: 0,
                    label: "No reward",
                    probabilityPct: 0,
                  }
                  next.games.wheel.freeSpinRewardPrizes[index] = {
                    ...current,
                    ...patch,
                  }
                })}
                onAdd={() => mutateSettings((next) => {
                  next.games.wheel.freeSpinRewardPrizes.push({
                    type: "points",
                    amount: 10,
                    label: "New Prize",
                    probabilityPct: 0,
                  })
                })}
                onRemove={(index) => mutateSettings((next) => {
                  next.games.wheel.freeSpinRewardPrizes = next.games.wheel.freeSpinRewardPrizes.filter((_, i) => i !== index)
                })}
                disabled={!canEdit}
              />
              <WelcomeSpinPrizeTableEditor
                title="Welcome spin rewards"
                status="active"
                rows={settings.games.welcomeSpin.prizes}
                onChange={(index, patch) => mutateSettings((next) => {
                  const current = next.games.welcomeSpin.prizes[index] ?? {
                    amount: 1,
                    label: "WIN!",
                    probabilityPct: 0,
                  }
                  next.games.welcomeSpin.prizes[index] = {
                    ...current,
                    ...patch,
                  }
                })}
                onAdd={() => mutateSettings((next) => {
                  next.games.welcomeSpin.prizes.push({
                    amount: 1,
                    label: "WIN!",
                    probabilityPct: 0,
                  })
                })}
                onRemove={(index) => mutateSettings((next) => {
                  next.games.welcomeSpin.prizes = next.games.welcomeSpin.prizes.filter((_, i) => i !== index)
                })}
                disabled={!canEdit}
              />
            </div>
          </AdminCard>
        </div>
      )}

      {activeSection === "throttles" && (
        <div className="space-y-4">
          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Burn Triggers and Mode Behavior" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <NumberField label="Normal max burn" value={settings.throttles.burnTriggersPct.normalMax} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.burnTriggersPct.normalMax = value })} disabled={!canEdit} />
              <NumberField label="Soft tighten max burn" value={settings.throttles.burnTriggersPct.softTightenMax} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.burnTriggersPct.softTightenMax = value })} disabled={!canEdit} />
              <NumberField label="Hard tighten max burn" value={settings.throttles.burnTriggersPct.hardTightenMax} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.burnTriggersPct.hardTightenMax = value })} disabled={!canEdit} />
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <ToggleField label="Normal: slot base table" value={settings.throttles.normal.slotUsesBaseTable} onChange={(value) => mutateSettings((next) => { next.throttles.normal.slotUsesBaseTable = value })} disabled={!canEdit} />
              <ToggleField label="Normal: wheel base table" value={settings.throttles.normal.wheelUsesBaseTable} onChange={(value) => mutateSettings((next) => { next.throttles.normal.wheelUsesBaseTable = value })} disabled={!canEdit} />
              <SelectField
                label="Normal mines mode"
                value={settings.throttles.normal.minesMode}
                options={["easy", "normal", "hard"]}
                onChange={(value) => mutateSettings((next) => { next.throttles.normal.minesMode = value })}
                disabled={!canEdit}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <h5 className="text-sm text-white/90 font-admin">Soft Tighten</h5>
                  <FeatureStatusBadge status="not_active" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <NumberField label="Slot top outcome multiplier" value={settings.throttles.softTighten.slotTopOutcomeMultiplier} step={0.01} onChange={(value) => mutateSettings((next) => { next.throttles.softTighten.slotTopOutcomeMultiplier = value })} disabled={!canEdit} />
                  <NumberField label="Mines target count" value={settings.throttles.softTighten.minesTargetMines} onChange={(value) => mutateSettings((next) => { next.throttles.softTighten.minesTargetMines = value })} disabled={!canEdit} />
                  <NumberField label="Cashout reduction min" value={settings.throttles.softTighten.minesCashoutReductionPctMin} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.softTighten.minesCashoutReductionPctMin = value })} disabled={!canEdit} />
                  <NumberField label="Cashout reduction max" value={settings.throttles.softTighten.minesCashoutReductionPctMax} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.softTighten.minesCashoutReductionPctMax = value })} disabled={!canEdit} />
                  <NumberField label="Wheel 3x token" value={settings.throttles.softTighten.wheelThreeXTokenPct} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.softTighten.wheelThreeXTokenPct = value })} disabled={!canEdit} />
                  <NumberField label="Shift to miss" value={settings.throttles.softTighten.wheelShiftToMissPct} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.softTighten.wheelShiftToMissPct = value })} disabled={!canEdit} />
                </div>
                <NumberListEditor
                  title="Soft slot affected multipliers"
                  status="not_active"
                  values={settings.throttles.softTighten.slotAffectedMultipliers}
                  onChange={(index, value) => mutateSettings((next) => { next.throttles.softTighten.slotAffectedMultipliers[index] = value })}
                  onAdd={() => mutateSettings((next) => { next.throttles.softTighten.slotAffectedMultipliers.push(0) })}
                  onRemove={(index) => mutateSettings((next) => { next.throttles.softTighten.slotAffectedMultipliers = next.throttles.softTighten.slotAffectedMultipliers.filter((_, i) => i !== index) })}
                  disabled={!canEdit}
                />
                <NumberListEditor
                  title="Soft redistribution multipliers"
                  status="not_active"
                  values={settings.throttles.softTighten.slotRedistributeToMultipliers}
                  onChange={(index, value) => mutateSettings((next) => { next.throttles.softTighten.slotRedistributeToMultipliers[index] = value })}
                  onAdd={() => mutateSettings((next) => { next.throttles.softTighten.slotRedistributeToMultipliers.push(0) })}
                  onRemove={(index) => mutateSettings((next) => { next.throttles.softTighten.slotRedistributeToMultipliers = next.throttles.softTighten.slotRedistributeToMultipliers.filter((_, i) => i !== index) })}
                  disabled={!canEdit}
                />
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <h5 className="text-sm text-white/90 font-admin">Hard Tighten</h5>
                  <FeatureStatusBadge status="not_active" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <NumberField label="20x multiplier" value={settings.throttles.hardTighten.slotTwentyXMultiplier} step={0.01} onChange={(value) => mutateSettings((next) => { next.throttles.hardTighten.slotTwentyXMultiplier = value })} disabled={!canEdit} />
                  <NumberField label="10x multiplier" value={settings.throttles.hardTighten.slotTenXMultiplier} step={0.01} onChange={(value) => mutateSettings((next) => { next.throttles.hardTighten.slotTenXMultiplier = value })} disabled={!canEdit} />
                  <NumberField label="Mines target count" value={settings.throttles.hardTighten.minesTargetMines} onChange={(value) => mutateSettings((next) => { next.throttles.hardTighten.minesTargetMines = value })} disabled={!canEdit} />
                  <NumberField label="Mines max pick cap" value={settings.throttles.hardTighten.minesMaxPickDepthUiCap} onChange={(value) => mutateSettings((next) => { next.throttles.hardTighten.minesMaxPickDepthUiCap = value })} disabled={!canEdit} />
                  <NumberField label="Wheel jackpot" value={settings.throttles.hardTighten.wheelJackpotPct} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.hardTighten.wheelJackpotPct = value })} disabled={!canEdit} />
                  <NumberField label="Wheel 3x token" value={settings.throttles.hardTighten.wheelThreeXTokenPct} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.hardTighten.wheelThreeXTokenPct = value })} disabled={!canEdit} />
                  <NumberField label="Shift to miss/20pts" value={settings.throttles.hardTighten.wheelShiftToMissOrTwentyPtsPct} suffix="%" onChange={(value) => mutateSettings((next) => { next.throttles.hardTighten.wheelShiftToMissOrTwentyPtsPct = value })} disabled={!canEdit} />
                </div>
                <NumberListEditor
                  title="Hard redistribution multipliers"
                  status="not_active"
                  values={settings.throttles.hardTighten.slotRedistributeToMultipliers}
                  onChange={(index, value) => mutateSettings((next) => { next.throttles.hardTighten.slotRedistributeToMultipliers[index] = value })}
                  onAdd={() => mutateSettings((next) => { next.throttles.hardTighten.slotRedistributeToMultipliers.push(0) })}
                  onRemove={(index) => mutateSettings((next) => { next.throttles.hardTighten.slotRedistributeToMultipliers = next.throttles.hardTighten.slotRedistributeToMultipliers.filter((_, i) => i !== index) })}
                  disabled={!canEdit}
                />
              </div>
            </div>

            <div className="mt-3">
              <ToggleField label="Points still issued during throttle" value={settings.throttles.pointsStillIssuedWhenThrottled} onChange={(value) => mutateSettings((next) => { next.throttles.pointsStillIssuedWhenThrottled = value })} disabled={!canEdit} />
            </div>
          </AdminCard>
        </div>
      )}

      {activeSection === "governance" && (
        <div className="space-y-4">
          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Guardrails and Integrity" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ToggleField label="Points can be gambled" value={settings.games.guardrails.pointsCanBeGambled} onChange={(value) => mutateSettings((next) => { next.games.guardrails.pointsCanBeGambled = value })} disabled={!canEdit} />
              <ToggleField label="Reward credits can be gambled" value={settings.games.guardrails.rewardCreditsCanBeGambled} onChange={(value) => mutateSettings((next) => { next.games.guardrails.rewardCreditsCanBeGambled = value })} disabled={!canEdit} />
              <ToggleField label="Wagers require owned points" value={settings.games.guardrails.wagersMustUseOwnedPoints} onChange={(value) => mutateSettings((next) => { next.games.guardrails.wagersMustUseOwnedPoints = value })} disabled={!canEdit} />
              <ToggleField label="Multipliers are consumable" value={settings.games.guardrails.multipliersAreConsumable} onChange={(value) => mutateSettings((next) => { next.games.guardrails.multipliersAreConsumable = value })} disabled={!canEdit} />
              <NumberField label="Multiplier expiry" value={settings.games.guardrails.multipliersExpireHours} suffix="hours" onChange={(value) => mutateSettings((next) => { next.games.guardrails.multipliersExpireHours = value })} disabled={!canEdit} />
              <SelectField
                label="Primary throttle lever"
                value={settings.games.guardrails.primaryThrottleLever}
                options={["mines", "slot", "wheel"]}
                onChange={(value) => mutateSettings((next) => { next.games.guardrails.primaryThrottleLever = value })}
                disabled={!canEdit}
              />
              <SelectField
                label="Primary excitement lever"
                value={settings.games.guardrails.primaryExcitementLever}
                options={["mines", "slot", "wheel"]}
                onChange={(value) => mutateSettings((next) => { next.games.guardrails.primaryExcitementLever = value })}
                disabled={!canEdit}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
              <ToggleField label="Fraud detection" value={settings.integrity.fraudDetectionEnabled} onChange={(value) => mutateSettings((next) => { next.integrity.fraudDetectionEnabled = value })} disabled={!canEdit} />
              <ToggleField label="Velocity anomaly flags" value={settings.integrity.velocityAnomalyFlagsEnabled} onChange={(value) => mutateSettings((next) => { next.integrity.velocityAnomalyFlagsEnabled = value })} disabled={!canEdit} />
              <ToggleField label="Manual ban workflow" value={settings.integrity.manualBanWorkflowEnabled} onChange={(value) => mutateSettings((next) => { next.integrity.manualBanWorkflowEnabled = value })} disabled={!canEdit} />
              <ToggleField label="Enforcement separate from economy" value={settings.integrity.keepEnforcementSeparateFromEconomyRules} onChange={(value) => mutateSettings((next) => { next.integrity.keepEnforcementSeparateFromEconomyRules = value })} disabled={!canEdit} />
            </div>
          </AdminCard>

          <AdminCard className="bg-white/[0.03] border-white/10">
            <SectionBlockTitle title="Internal Dashboards" status="not_active" className="mb-4" />
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
              <StringListEditor
                title="Point velocity metrics"
                status="not_active"
                values={settings.internalDashboards.pointVelocityMetrics}
                onChange={(index, value) => mutateSettings((next) => { next.internalDashboards.pointVelocityMetrics[index] = value })}
                onAdd={() => mutateSettings((next) => { next.internalDashboards.pointVelocityMetrics.push("metric") })}
                onRemove={(index) => mutateSettings((next) => { next.internalDashboards.pointVelocityMetrics = next.internalDashboards.pointVelocityMetrics.filter((_, i) => i !== index) })}
                disabled={!canEdit}
              />
              <StringListEditor
                title="Reward conversion metrics"
                status="not_active"
                values={settings.internalDashboards.rewardCreditConversionMetrics}
                onChange={(index, value) => mutateSettings((next) => { next.internalDashboards.rewardCreditConversionMetrics[index] = value })}
                onAdd={() => mutateSettings((next) => { next.internalDashboards.rewardCreditConversionMetrics.push("metric") })}
                onRemove={(index) => mutateSettings((next) => { next.internalDashboards.rewardCreditConversionMetrics = next.internalDashboards.rewardCreditConversionMetrics.filter((_, i) => i !== index) })}
                disabled={!canEdit}
              />
              <StringListEditor
                title="Behavior heatmap metrics"
                status="not_active"
                values={settings.internalDashboards.repBehaviorHeatmapMetrics}
                onChange={(index, value) => mutateSettings((next) => { next.internalDashboards.repBehaviorHeatmapMetrics[index] = value })}
                onAdd={() => mutateSettings((next) => { next.internalDashboards.repBehaviorHeatmapMetrics.push("metric") })}
                onRemove={(index) => mutateSettings((next) => { next.internalDashboards.repBehaviorHeatmapMetrics = next.internalDashboards.repBehaviorHeatmapMetrics.filter((_, i) => i !== index) })}
                disabled={!canEdit}
              />
            </div>
          </AdminCard>
        </div>
      )}
    </div>
  )
}
